import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore, useConfirmationModalStore } from '../../../../../stores';
import Modal from '../../controls/Modal';
import { CHALLENGE_HOME_NAME } from './challenges/challengeUtils';

const categoryTitle = (category) =>
    category?.displayName === CHALLENGE_HOME_NAME ? 'Челленджи' : (category?.displayName || 'Без категории');

// Модалка импорта заданий из пака шаблонов: первичное копирование и синхронизация (повторный импорт).
export default function ImportModal({ smenaId, onSuccess, onClose }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const { open: openConfirm } = useConfirmationModalStore();
    const { data: packs = [] } = useApiQuery(['neighbor', 'template-packs'], '/api/neighbor/admin/task-template-packs/list-all');
    const [sel, setSel] = useState(null);

    const go = async () => {
        if (!sel) return;
        show();
        try {
            await apiClient.post(`/api/neighbor/admin/task-pack-operations/copy-pack-to-smena?packId=${sel}&smenaId=${smenaId}`);
            enqueueSnackbar('Пак импортирован', { variant: 'success' });
            onSuccess();
            onClose();
        } catch (e) {
            if (e.response?.status === 409) {
                enqueueSnackbar('Пак уже импортирован в смену — можно синхронизировать', { variant: 'warning' });
            } else {
                enqueueSnackbar(e.response?.data?.message || 'Ошибка импорта', { variant: 'error' });
            }
        } finally { hide(); }
    };

    const sync = async () => {
        show();
        try {
            const res = await apiClient.post(`/api/neighbor/admin/task-pack-operations/sync-pack-to-smena?packId=${sel}&smenaId=${smenaId}`);
            const summary = (res.data || [])
                .map(r => `${categoryTitle(r.category)}: добавлено ${r.tasksCreated}, обновлено ${r.tasksUpdated}`)
                .join('; ');
            enqueueSnackbar(summary ? `Пак синхронизирован. ${summary}` : 'Пак синхронизирован', { variant: 'success', autoHideDuration: 8000 });
            onSuccess();
            onClose();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Ошибка синхронизации', { variant: 'error' });
        } finally { hide(); }
    };

    const confirmSync = () => openConfirm(
        <div className="confirm-msg">
            <b>Синхронизировать смену с паком?</b>
            <p>Параметры всех заданий этой смены, скопированных из пака, будут перезаписаны значениями из пака — ручные правки (цены, описания, лимиты) потеряются.</p>
            <p>Не затрагиваются: счётчики покупок и активность заданий, задания, созданные вручную, и задания, чьи шаблоны удалены из пака.</p>
        </div>,
        sync,
        { confirmText: 'Синхронизировать' }
    );

    return (
        <Modal
            open
            kicker="Импорт"
            title="Импорт заданий из пака"
            onClose={onClose}
            footer={(
                <>
                    <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
                    <button type="button" className="btn btn-ghost" onClick={confirmSync} disabled={!sel}>Синхронизировать с паком</button>
                    <button type="button" className="btn btn-primary" onClick={go} disabled={!sel}>Импортировать весь пак</button>
                </>
            )}
        >
            {packs.length > 0 ? (
                <div className="sp-packs">
                    {packs.map(p => (
                        <button key={p.id} type="button" className={`sp-chip${sel === p.id ? ' on' : ''}`} onClick={() => setSel(p.id)}>{p.name}</button>
                    ))}
                </div>
            ) : <div className="bt-empty">Нет паков шаблонов</div>}
        </Modal>
    );
}

ImportModal.propTypes = {
    smenaId: PropTypes.number,
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

ImportModal.defaultProps = { smenaId: null };
